import React, { useState, useEffect, useCallback } from 'react';
import { Image, Button, Typography, Modal, Spin, Row, Col, Card, Badge, Tooltip } from 'antd';
import { 
  DownloadOutlined, 
  EyeOutlined, 
  FileOutlined, 
  PlayCircleOutlined, 
  FullscreenOutlined,
  FileImageOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import { TicketFile } from '../types/ticket';
import { apiService } from '../services/api';

const { Text } = Typography;

interface FileViewerProps {
  files: TicketFile[];
  token?: string | null;
}

const FileViewer: React.FC<FileViewerProps> = ({ files, token }) => {
  const [fileUrls, setFileUrls] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState<{ url: string; type: string; name: string }>({
    url: '',
    type: '',
    name: ''
  });

  const isImage = (fileType: string) => {
    return fileType.startsWith('image/');
  };

  const isVideo = (fileType: string) => {
    return fileType.startsWith('video/');
  };

  const isPdf = (fileType: string) => {
    return fileType === 'application/pdf';
  };

  const getFileIcon = (fileType: string) => {
    if (isImage(fileType)) return <FileImageOutlined />;
    if (isVideo(fileType)) return <VideoCameraOutlined />;
    if (isPdf(fileType)) return <FilePdfOutlined />;
    return <FileTextOutlined />;
  };

  const getFileColor = (fileType: string) => {
    if (isImage(fileType)) return '#52c41a'; // green
    if (isVideo(fileType)) return '#1890ff'; // blue
    if (isPdf(fileType)) return '#fa541c'; // orange
    return '#8c8c8c'; // gray
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const loadFileUrl = useCallback(async (file: TicketFile) => {
    if (fileUrls[file.id]) return;

    setLoading(prev => ({ ...prev, [file.id]: true }));
    setErrors(prev => ({ ...prev, [file.id]: '' }));

    try {
      const url = await apiService.getFileStream(file.id, token);
      setFileUrls(prev => ({ ...prev, [file.id]: url }));
    } catch (error: any) {
      setErrors(prev => ({ 
        ...prev, 
        [file.id]: error.response?.data?.message || 'Failed to load file' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, [file.id]: false }));
    }
  }, [fileUrls, token]);

  const handlePreview = (file: TicketFile) => {
    const fileUrl = fileUrls[file.id];
    if (!fileUrl) return;
    
    setPreviewContent({
      url: fileUrl,
      type: file.fileType,
      name: file.name
    });
    setPreviewVisible(true);
  };

  const handleDownload = async (file: TicketFile) => {
    try {
      await apiService.downloadFile(file.id, file.name, token);
    } catch (error: any) {
      console.error('Download failed:', error);
    }
  };

  useEffect(() => {
    // Preload all files for better user experience
    files.forEach(file => {
      loadFileUrl(file);
    });
  }, [files, loadFileUrl]);

  const renderFileItem = (file: TicketFile) => {
    const isLoading = loading[file.id];
    const error = errors[file.id];
    const fileUrl = fileUrls[file.id];
    const isImageFile = isImage(file.fileType);
    const isVideoFile = isVideo(file.fileType);
    const isPdfFile = isPdf(file.fileType);
    const fileColor = getFileColor(file.fileType);

    return (
      <Col xs={8} sm={6} md={4} key={file.id}>
        <Card 
          hoverable
          className="file-card"
          cover={
            <div className="file-preview">
              {isLoading ? (
                <div className="file-loading">
                  <Spin size="small" />
                </div>
              ) : error ? (
                <div className="file-error">
                  <FileOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
                </div>
              ) : isImageFile && fileUrl ? (
                <div 
                  className="file-image-container"
                  onClick={() => handlePreview(file)}
                >
                  <Image
                    src={fileUrl}
                    alt={file.name}
                    preview={false}
                    className="file-image"
                  />
                </div>
              ) : isVideoFile && fileUrl ? (
                <div 
                  className="file-video-container"
                  onClick={() => handlePreview(file)}
                >
                  <div className="file-video-thumbnail">
                    <video
                      src={fileUrl}
                      preload="metadata"
                      className="file-video"
                    />
                    <div className="file-video-overlay">
                      <PlayCircleOutlined className="file-video-play" />
                    </div>
                  </div>
                </div>
              ) : (
                <div 
                  className="file-icon-container"
                  style={{ backgroundColor: `${fileColor}10` }}
                  onClick={() => fileUrl && handlePreview(file)}
                >
                  <div className="file-icon" style={{ color: fileColor }}>
                    {getFileIcon(file.fileType)}
                  </div>
                </div>
              )}
            </div>
          }
          bodyStyle={{ padding: '4px' }}
        >
          <div className="file-info">
            <Tooltip title={file.name}>
              <Text ellipsis className="file-name" style={{ textAlign: 'left', display: 'block' }}>{file.name}</Text>
            </Tooltip>
            <div className="file-actions">
              <Text type="secondary" className="file-size">{formatFileSize(file.fileSize)}</Text>
              <div>
                <Button
                  type="text"
                  size="small"
                  icon={isVideoFile ? <PlayCircleOutlined /> : <EyeOutlined />}
                  onClick={() => handlePreview(file)}
                  disabled={!fileUrl || isLoading}
                  className="file-action-button"
                />
                <Button
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(file)}
                  className="file-action-button"
                />
              </div>
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  return (
    <>
      <Row gutter={[8, 8]} className="file-grid">
        {files.map(renderFileItem)}
      </Row>

      <Modal
        open={previewVisible}
        title={
          <div className="preview-header">
            <Text ellipsis className="preview-title">{previewContent.name}</Text>
            {isVideo(previewContent.type) && (
              <Button 
                icon={<FullscreenOutlined />} 
                type="text"
                onClick={() => {
                  const videoElement = document.querySelector('.preview-video') as HTMLVideoElement;
                  if (videoElement) {
                    if (videoElement.requestFullscreen) {
                      videoElement.requestFullscreen();
                    } else if ((videoElement as any).webkitRequestFullscreen) {
                      (videoElement as any).webkitRequestFullscreen();
                    } else if ((videoElement as any).mozRequestFullScreen) {
                      (videoElement as any).mozRequestFullScreen();
                    }
                  }
                }}
                className="preview-fullscreen"
              >
                Fullscreen
              </Button>
            )}
          </div>
        }
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="95%"
        centered
        className="preview-modal"
        bodyStyle={{ padding: isVideo(previewContent.type) ? '0' : '24px' }}
        destroyOnClose
        maskClosable={true}
        closable={true}
        zIndex={1000}
      >
        {previewContent.url && (
          <div className="preview-content">
            {isVideo(previewContent.type) ? (
              <video
                className="preview-video"
                controls
                autoPlay
                src={previewContent.url}
                style={{ width: '100%', maxHeight: '70vh' }}
                playsInline
                webkit-playsinline="true"
              >
                Your browser does not support the video tag.
              </video>
            ) : isImage(previewContent.type) ? (
              <div style={{ textAlign: 'center' }}>
                <Image
                  src={previewContent.url}
                  alt={previewContent.name}
                  className="preview-image"
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                  preview={false}
                />
              </div>
            ) : isPdf(previewContent.type) ? (
              <iframe
                src={previewContent.url}
                className="preview-pdf"
                title={previewContent.name}
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
            ) : (
              <div className="preview-unsupported">
                <FileOutlined className="preview-unsupported-icon" />
                <Text>This file type cannot be previewed. Please download it to view.</Text>
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    const file = files.find(f => f.name === previewContent.name);
                    if (file) handleDownload(file);
                  }}
                >
                  Download
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default FileViewer;